// Azure Resource Manager activity log helpers.
// Parse Azure resource IDs into a scope+artifact pair, label/format the scope
// for display, and supply a short hint that explains the gap between what the
// activity log records and what the analyst actually needs to check. Moved out
// of AlertsPage.jsx as part of the v1.54.0 Phase 2 three-way parity migration.

export function parseAzureAuthScope(resourceId) {
  if (!resourceId || typeof resourceId !== 'string') return null;
  const marker = '/providers/Microsoft.Authorization/';
  const idx = resourceId.lastIndexOf(marker);
  if (idx === -1) return null;
  const scopePath = resourceId.slice(0, idx);
  const artifactSegs = resourceId.slice(idx + marker.length).split('/').filter(Boolean);
  const artifactType = artifactSegs[0] || null;
  const artifactId = artifactSegs[1] || null;
  const segs = scopePath.split('/').filter(Boolean);
  const scope = { level: 'unknown', subscriptionId: null, resourceGroup: null, provider: null, typeName: null, name: null, nestedPath: null };
  let i = 0;
  if (segs[i] === 'subscriptions' && segs[i + 1]) {
    scope.subscriptionId = segs[i + 1];
    scope.level = 'subscription';
    i += 2;
  }
  if (segs[i] === 'resourceGroups' && segs[i + 1]) {
    scope.resourceGroup = segs[i + 1];
    scope.level = 'resourceGroup';
    i += 2;
  }
  if (segs[i] === 'providers' && segs[i + 1] && segs[i + 2] && segs[i + 3]) {
    scope.provider = segs[i + 1];
    scope.typeName = segs[i + 2];
    scope.name = segs[i + 3];
    scope.level = 'resource';
    i += 4;
    const extra = [];
    while (segs[i] && segs[i + 1]) {
      extra.push(`${segs[i]}/${segs[i + 1]}`);
      i += 2;
    }
    if (extra.length > 0) scope.nestedPath = extra.join('/');
  }
  return { scope, artifactType, artifactId };
}

export function scopeTargetTypeLabel(scope) {
  if (scope.provider && scope.typeName) {
    const nested = scope.nestedPath ? ` / ${scope.nestedPath.split('/').filter((_, i) => i % 2 === 0).join(' / ')}` : '';
    return `${scope.provider}/${scope.typeName}${nested}`;
  }
  if (scope.level === 'resourceGroup') return 'Resource Group';
  if (scope.level === 'subscription') return 'Subscription';
  return '—';
}

export function scopeTargetName(scope) {
  if (scope.nestedPath) {
    const parts = scope.nestedPath.split('/').filter(Boolean);
    const lastName = parts[parts.length - 1];
    return `${scope.name} / ${lastName}`;
  }
  if (scope.name) return scope.name;
  if (scope.resourceGroup) return scope.resourceGroup;
  if (scope.subscriptionId) return scope.subscriptionId;
  return '—';
}

export function authorizationHint(artifactType) {
  switch (artifactType) {
    case 'roleAssignments':
      return 'Azure Activity does not log the role definition or the assigned principal. Open Access Control (IAM) on the target resource to see who received which role.';
    case 'roleDefinitions':
      return 'Role permission details are not in the activity log. Check Custom Roles on the target scope to see the granted actions.';
    case 'policyAssignments':
      return 'Policy parameters and enforcement scope are not in the activity log. Check Policy assignments on the target resource.';
    case 'denyAssignments':
      return 'Deny assignment details (principals, excluded actions) are not in the activity log. Check Access Control (IAM) → Deny assignments on the target resource.';
    case 'locks':
      return 'Lock level (ReadOnly/CanNotDelete) is not in the activity log. Check Locks on the target resource.';
    default:
      return 'Details of this authorization change are not captured in the activity log. Review IAM/Policy on the target resource.';
  }
}
